import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './SignUp.css';
import logo from './logo.png';
import teacherImage from './teacher-auth-image.jpg';
import studentImage from './student-auth-image.jpg';
import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';
import bcrypt from 'bcryptjs';
import { validateReferralCode, completeReferralSignup } from './xpService';
import { isTeacherSubdomain } from './utils/subdomainDetector';

// Translations
const translations = {
  EN: {
    title: 'Create your account',
    subtitle: "Choose a secure password that you don't use anywhere else.",
    subtitleTeacher: "Sign up as a teacher - Choose a secure password that you don't use anywhere else.",
    subtitleStudent: "Sign up as a student - Choose a secure password that you don't use anywhere else.",
    teacher: 'Teacher',
    student: 'Student',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Enter your full name',
    email: 'Email Address',
    emailPlaceholder: 'Enter your email',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    phone: 'Phone Number',
    phonePlaceholder: '12345678',
    gender: 'Gender',
    selectGender: 'Select gender',
    male: 'Male',
    female: 'Female',
    inviteCode: 'Invite code (optional)',
    inviteCodePlaceholder: 'Enter invite code if you have one',
    agreeTerms: 'I agree to the',
    userAgreement: 'Taealam User Agreement',
    privacyPolicy: 'Privacy Policy',
    continue: 'Continue',
    creating: 'Creating...',
    alreadyAccount: 'Already have an account?',
    signIn: 'Sign in',
    req12Chars: 'At least 12 characters',
    reqLowercase: 'At least 1 lowercase character (a-z)',
    reqUppercase: 'At least 1 uppercase character (A-Z)',
    reqNumber: 'At least 1 number (0-9)',
    checking: 'Checking...',
    invalidCode: 'Invalid referral code',
    codeNotFound: 'Referral code not found',
    codeMustBe8: 'Referral code must be 8 characters',
    referralFrom: 'Referral from',
    aFriend: 'a friend',
    fillAllFields: 'Please fill in all required fields.',
    invalidPhone: 'Please enter a valid phone number (8-15 digits).',
    acceptTerms: 'You must accept the Terms & Privacy Policy.',
    strongerPassword: 'Please choose a stronger password.',
    accountCreated: 'Account created successfully!',
    friendEarned: 'Account created! Your friend earned 200 XP for referring you!',
    tooQuickly: 'You are signing up too quickly. Please wait 36 seconds and try again.',
    signUpFailed: 'Sign up failed.',
    teacherSignUpOnly: 'Teacher Sign Up Only',
    signingUp: 'Signing up...',
  },
  AR: {
    title: 'إنشاء حسابك',
    subtitle: 'اختر كلمة مرور آمنة لا تستخدمها في أي مكان آخر.',
    subtitleTeacher: 'إنشاء حساب كمعلم - اختر كلمة مرور آمنة لا تستخدمها في أي مكان آخر.',
    subtitleStudent: 'إنشاء حساب كطالب - اختر كلمة مرور آمنة لا تستخدمها في أي مكان آخر.',
    teacher: 'معلم',
    student: 'طالب',
    fullName: 'الاسم الكامل',
    fullNamePlaceholder: 'أدخل اسمك الكامل',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    password: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    phone: 'رقم الهاتف',
    phonePlaceholder: '12345678',
    gender: 'الجنس',
    selectGender: 'اختر الجنس',
    male: 'ذكر',
    female: 'أنثى',
    inviteCode: 'رمز الدعوة (اختياري)',
    inviteCodePlaceholder: 'أدخل رمز الدعوة إن وجد',
    agreeTerms: 'أوافق على',
    userAgreement: 'اتفاقية مستخدم تعلّم',
    privacyPolicy: 'سياسة الخصوصية',
    continue: 'متابعة',
    creating: 'جاري الإنشاء...',
    alreadyAccount: 'لديك حساب بالفعل؟',
    signIn: 'تسجيل الدخول',
    req12Chars: '12 حرفاً على الأقل',
    reqLowercase: 'حرف صغير واحد على الأقل (a-z)',
    reqUppercase: 'حرف كبير واحد على الأقل (A-Z)',
    reqNumber: 'رقم واحد على الأقل (0-9)',
    checking: 'جاري التحقق...',
    invalidCode: 'رمز الدعوة غير صالح',
    codeNotFound: 'رمز الدعوة غير موجود',
    codeMustBe8: 'رمز الدعوة يجب أن يكون 8 أحرف',
    referralFrom: 'دعوة من',
    aFriend: 'صديق',
    fillAllFields: 'يرجى ملء جميع الحقول المطلوبة.',
    invalidPhone: 'يرجى إدخال رقم هاتف صالح (8-15 رقم).',
    acceptTerms: 'يجب الموافقة على الشروط وسياسة الخصوصية.',
    strongerPassword: 'يرجى اختيار كلمة مرور أقوى.',
    accountCreated: 'تم إنشاء الحساب بنجاح!',
    friendEarned: 'تم إنشاء الحساب! حصل صديقك على 200 نقطة خبرة!',
    tooQuickly: 'أنت تسجل بسرعة كبيرة. يرجى الانتظار 36 ثانية والمحاولة مرة أخرى.',
    signUpFailed: 'فشل التسجيل.',
    teacherSignUpOnly: 'تسجيل المعلمين فقط',
    signingUp: 'جاري التسجيل...',
  }
};

// Password requirements checker
function checkPasswordRequirements(password) {
  return {
    length: password.length >= 12,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
  };
}

// Lightweight password evaluator
function evaluatePasswordStrength(password, context) {
  const result = { score: 0, warnings: [], suggestions: [], breached: false };
  if (!password) return result;

  const lengthScore = password.length >= 12 ? 2 : password.length >= 10 ? 1 : 0;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce((sum, re) => sum + (re.test(password) ? 1 : 0), 0);
  const classScore = classes >= 3 ? 2 : classes >= 2 ? 1 : 0;

  const repeats = /(.)\1{2,}/.test(password) ? -1 : 0;
  const walks = /(0123|1234|2345|3456|4567|5678|6789|abcd|qwer|asdf|zxcv)/i.test(password) ? -1 : 0;
  const personal = [];
  if (context?.email && password.toLowerCase().includes(String(context.email).split('@')[0].toLowerCase())) personal.push('email username');
  if (context?.full_name && password.toLowerCase().includes(String(context.full_name).split(' ')[0].toLowerCase())) personal.push('name');
  const personalPenalty = personal.length > 0 ? -1 : 0;

  let score = lengthScore + classScore + repeats + walks + personalPenalty;
  score = Math.max(0, Math.min(4, score));
  result.score = score;

  return result;
}

export default function SignUp() {
  // Auto-detect role from subdomain
  const onTeacherSubdomain = isTeacherSubdomain();
  const role = onTeacherSubdomain ? 'teacher' : 'student';
  const [searchParams, setSearchParams] = useSearchParams();
  const isOAuthCallback = searchParams.get('oauth_callback') === 'true';
  const [showPassword, setShowPassword] = useState(false);
  // Get language from localStorage to stay consistent with App.js
  const [language, setLanguage] = useState(() => {
    const savedLang = localStorage.getItem('taealam_language');
    return savedLang === 'ar' ? 'AR' : 'EN';
  });
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    gender: ''
  });
  const [passwordInfo, setPasswordInfo] = useState({ score: 0, warnings: [], suggestions: [], breached: false });
  const [passwordReqs, setPasswordReqs] = useState({ length: false, lowercase: false, uppercase: false, number: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');
  const navigate = useNavigate();


  const toggleLanguage = () => {
    const newLang = language === 'EN' ? 'AR' : 'EN';
    setLanguage(newLang);
    // Save to localStorage to sync with App.js
    localStorage.setItem('taealam_language', newLang === 'AR' ? 'ar' : 'en');
    // Update document direction
    document.documentElement.dir = newLang === 'AR' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang === 'AR' ? 'ar' : 'en';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'terms') {
      setAcceptedTerms(checked);
    } else if (name === 'privacy') {
      setAcceptedPrivacy(checked);
    } else {
      // For phone input, strip non-digit characters and limit to 8 digits (Bahrain numbers)
      let sanitizedValue = value;
      if (name === 'phone') {
        sanitizedValue = value.replace(/\D/g, '').slice(0, 8); // Bahrain numbers are 8 digits
      }
      const next = { ...form, [name]: sanitizedValue };
      setForm(next);
      if (name === 'password') {
        setPasswordReqs(checkPasswordRequirements(sanitizedValue));
        setPasswordInfo(evaluatePasswordStrength(sanitizedValue, next));
      }
      if (name === 'email' || name === 'full_name') {
        setPasswordInfo(evaluatePasswordStrength(next.password, next));
      }
    }
  };

  const handleReferralCodeChange = async (e) => {
    const code = e.target.value.trim().toUpperCase();
    setReferralCode(code);
    setReferralValid(null);
    setReferralMessage('');

    if (code.length === 8) {
      setReferralValidating(true);
      const { data, error } = await validateReferralCode(code);
      setReferralValidating(false);

      if (error) {
        setReferralValid(false);
        setReferralMessage('Invalid referral code');
      } else if (data && data.valid) {
        setReferralValid(true);
        setReferralMessage(`Referral from ${data.referrerName || 'a friend'}`);
      } else {
        setReferralValid(false);
        setReferralMessage('Referral code not found');
      }
    } else if (code.length > 0) {
      setReferralMessage('Referral code must be 8 characters');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    // Validate Bahrain phone number (8 digits)
    const phoneRegex = /^\d{8}$/;
    if (!form.full_name || !form.email || !form.password || !form.phone || !form.gender) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!phoneRegex.test(form.phone)) {
      setError('Please enter a valid Bahrain phone number (8 digits).');
      return;
    }
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('You must accept the Terms & Privacy Policy.');
      return;
    }
    const strength = evaluatePasswordStrength(form.password, form);
    if (strength.score < 3) {
      setError('Please choose a stronger password.');
      return;
    }

    // SUBDOMAIN VALIDATION - Prevent student signups on teacher subdomain
    if (onTeacherSubdomain && role === 'student') {
      setError('Student accounts cannot be created on the teacher subdomain. Please visit the main domain to sign up as a student.');
      return;
    }

    setLoading(true);
    try {
      // Format phone number with +973 prefix
      const formattedPhone = form.phone.startsWith('+973') 
        ? form.phone 
        : `+973${form.phone}`;

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            full_name: form.full_name,
            phone: formattedPhone,
          },
          emailRedirectTo: undefined
        }
      });
      
      if (authError) throw authError;
      
      const user = authData?.user;
      if (!user) throw new Error('User creation failed');

      await supabase.auth.updateUser({
        data: {
          full_name: form.full_name,
          phone: formattedPhone,
        }
      });
      
      const tableName = role === 'teacher' ? 'Teachers' : 'students';
      const userData = {
        uuid_id: user.id,
        full_name: form.full_name,
        email: form.email,
        phone: formattedPhone,
        whatsapp_phone: formattedPhone, // Also save to whatsapp_phone column
        gender: form.gender,
        created_at: new Date().toISOString()
      };

      if (role === 'teacher') {
        userData.confirmation_completed = false;
      }

      const { error: supabaseError } = await supabase
        .from(tableName)
        .insert([userData]);
      if (supabaseError) throw supabaseError;

      // Add 15% first session discount for students
      if (role === 'student') {
        try {
          const discountData = {
            user_id: user.id,
            user_email: form.email,
            discount_percentage: 15,
            discount_type: 'first_session',
            description: 'Welcome discount for first tutoring session',
            valid_from: new Date().toISOString(),
            valid_until: null,
            used: false,
            created_at: new Date().toISOString()
          };

          const { error: discountError } = await supabase
            .from('user_discounts')
            .insert([discountData]);

          if (discountError) {
            console.log('Discount creation failed (non-critical):', discountError);
          }
        } catch (discountErr) {
          console.log('Discount insert error (non-critical):', discountErr);
        }
      }

      try {
        const emailEndpoint = role === 'teacher'
          ? `${getApiBaseUrl()}/api/send-teacher-signup`
          : `${getApiBaseUrl()}/send-welcome`;

        await fetch(emailEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: form.email, name: form.full_name, phone: form.phone }),
        });
      } catch (emailError) {
        console.log('Welcome email failed (non-critical):', emailError);
      }

      if (role === 'student' && referralCode && referralValid) {
        try {
          const { data: referralData, error: referralError } = await completeReferralSignup(
            referralCode,
            form.email,
            user.id
          );

          if (referralData && !referralError) {
            setSuccess(`Account created! Your friend earned 200 XP for referring you!`);
          } else {
            setSuccess('Account created successfully!');
          }
        } catch (referralError) {
          setSuccess('Account created successfully!');
        }
      } else {
        setSuccess('Account created successfully!');
      }

      setForm({ full_name: '', email: '', password: '', phone: '', gender: '' });
      setReferralCode('');
      setReferralValid(null);
      setReferralMessage('');
      setAcceptedTerms(false);
      setAcceptedPrivacy(false);
      // Redirect students to Coming Soon page, teachers to Sign In
      if (role === 'student') {
        setTimeout(() => navigate('/coming-soon'), 2000);
      } else {
        setTimeout(() => navigate('/signin'), 2000);
      }
    } catch (err) {
      if (err.status === 429 || (err.message && err.message.includes('security purposes'))) {
        setError('You are signing up too quickly. Please wait 36 seconds and try again.');
      } else {
        setError(err.message || 'Sign up failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const allReqsMet = passwordReqs.length && passwordReqs.lowercase && passwordReqs.uppercase && passwordReqs.number;
  const t = translations[language];
  const isRTL = language === 'AR';

  return (
    <div className={`auth-container ${isRTL ? 'rtl' : ''}`}>
      {/* Left Side - Form */}
      <div className="auth-form-side">
        <div className="auth-form-wrapper">
          {/* Header with Logo and Language Toggle */}
          <div className="auth-header">
            <img
              src={logo}
              alt="Taealam Logo"
              className="auth-logo"
              onClick={() => navigate('/')}
            />
            <button
              className="language-toggle"
              onClick={toggleLanguage}
              aria-label="Toggle language"
            >
              {language === 'EN' ? 'AR' : 'EN'}
            </button>
          </div>

          {/* Title */}
          <h1 className="auth-title">{t.title}</h1>
          <p className="auth-subtitle">
            {onTeacherSubdomain ? t.subtitleTeacher : t.subtitleStudent}
          </p>

          <form className="auth-form" onSubmit={handleSubmit}>
              {/* Row 1: Name and Email */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t.fullName}</label>
                  <input
                    type="text"
                    name="full_name"
                    placeholder={t.fullNamePlaceholder}
                    value={form.full_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t.email}</label>
                  <input
                    type="email"
                    name="email"
                    placeholder={t.emailPlaceholder}
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

            {/* Row 2: Password */}
            <div className="form-group">
              <label>{t.password}</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t.passwordPlaceholder}
                  value={form.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="password-requirements">
              <div className={`req-item ${passwordReqs.length ? 'met' : ''}`}>
                <span className="req-circle"></span>
                {t.req12Chars}
              </div>
              <div className={`req-item ${passwordReqs.lowercase ? 'met' : ''}`}>
                <span className="req-circle"></span>
                {t.reqLowercase}
              </div>
              <div className={`req-item ${passwordReqs.number ? 'met' : ''}`}>
                <span className="req-circle"></span>
                {t.reqNumber}
              </div>
              <div className={`req-item ${passwordReqs.uppercase ? 'met' : ''}`}>
                <span className="req-circle"></span>
                {t.reqUppercase}
              </div>
            </div>

            {/* Row 3: Phone and Gender */}
            <div className="form-row">
              <div className="form-group">
                <label>{t.phone}</label>
                <div style={{ 
                  position: 'relative', 
                  display: 'flex', 
                  alignItems: 'stretch',
                  width: '100%'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 16px', 
                    background: '#f3f4f6', 
                    border: '2px solid var(--input-border)',
                    borderRight: 'none',
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#374151',
                    whiteSpace: 'nowrap',
                    direction: 'ltr',
                    unicodeBidi: 'isolate'
                  }}>
                    +973
                  </div>
                  <input 
                    type="text" 
                    name="phone" 
                    placeholder={t.phonePlaceholder} 
                    value={form.phone} 
                    onChange={handleChange}
                    maxLength={8}
                    style={{
                      flex: 1,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderLeft: 'none',
                      paddingLeft: '12px',
                      height: '40px'
                    }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t.gender}</label>
                <select name="gender" value={form.gender} onChange={handleChange} className={form.gender === '' ? 'placeholder' : ''}>
                  <option value="" disabled>{t.selectGender}</option>
                  <option value="Male">{t.male}</option>
                  <option value="Female">{t.female}</option>
                </select>
              </div>
            </div>

            {/* Referral Code (Students only) */}
            {role === 'student' && (
              <div className="form-group">
                <label>{t.inviteCode}</label>
                <div className="referral-input-wrapper">
                  <input
                    type="text"
                    name="referralCode"
                    placeholder={t.inviteCodePlaceholder}
                    style={{ textTransform: 'none' }}
                    value={referralCode}
                    onChange={handleReferralCodeChange}
                    maxLength={8}
                    className={referralValid === true ? 'valid' : referralValid === false ? 'invalid' : ''}
                  />
                  {referralValidating && <span className="referral-status">{t.checking}</span>}
                  {referralValid === true && <span className="referral-status success">✓</span>}
                  {referralValid === false && <span className="referral-status error">✗</span>}
                </div>
                {referralMessage && (
                  <span className={`referral-message ${referralValid === true ? 'success' : 'error'}`}>
                    {referralMessage}
                  </span>
                )}
              </div>
            )}

            {/* Agreements */}
            <div className="agreements">
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  name="terms" 
                  checked={acceptedTerms} 
                  onChange={handleChange} 
                />
                <span className="checkbox-custom"></span>
                {t.agreeTerms} <a href="/terms">{t.userAgreement}</a>
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  name="privacy" 
                  checked={acceptedPrivacy} 
                  onChange={handleChange} 
                />
                <span className="checkbox-custom"></span>
                {t.agreeTerms} <a href="/privacy">{t.privacyPolicy}</a>
              </label>
            </div>

            {/* Error/Success Messages */}
            {error && <div className="auth-error">{error}</div>}
            {success && <div className="auth-success">{success}</div>}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading || !form.full_name || !form.email || !form.password || !form.phone || !form.gender || !acceptedTerms || !acceptedPrivacy || !allReqsMet}
            >
              {loading ? t.creating : t.continue}
            </button>
          </form>


          {/* Sign In Link */}
          <p className="auth-switch-link">
            {t.alreadyAccount} <a href="/signin">{t.signIn}</a>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="auth-image-side">
        <img 
          src={role === 'teacher' ? teacherImage : studentImage} 
          alt={role === 'teacher' ? 'Teacher Dashboard' : 'Student Learning'} 
          className="auth-hero-image" 
        />
        <div className="auth-image-overlay"></div>
      </div>
    </div>
  );
}

// Icons
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

